import type { tFreeTierCounter } from './Wallet.types';

/**
 * RET-2319 · la cota de vejez de la lectura del contador de la franja, y su
 * predicado.
 *
 * Vive en un módulo puro porque lo consultan DOS capas que no pueden separarse
 * —el provider, que degrada el `ready` cuando la cota se cumple, y el sello, que
 * juzga la vejez con el reloj de SU propio render—, y el dueño de la cota tiene
 * que ser uno solo. El sello necesita juzgarla
 * por su cuenta porque hay un frame que el provider no alcanza: `Date.now()` no
 * puede ser dependencia de un `useMemo`, y cuando el compositor se remonta no
 * cambia ninguna dep del memo del contexto —un hijo que monta no re-renderiza a
 * su padre, y la presencia se registra en un `useEffect` PASIVO
 * (`hooks/useWapiComposerPresence.ts`)—, así que ese commit sirve el valor
 * cacheado. Ver `docs/loop/RET-2319-estado.md`, vuelta 3.
 *
 * Dos cotas que puedan separarse serían una clase de defecto nueva: la del
 * provider degradando a los 120 s y la del sello creyendo otra cosa.
 */

/**
 * DOS ciclos del poll de 60 s.
 *
 * Conservar el contador cuando el compositor se va evita el parpadeo de un
 * remontaje inmediato, pero sin cota cambia un falso «Con costo» por un posible
 * falso «Gratis», que es el lado que el ruling de la afirmación asimétrica
 * prohíbe: cerrar el chat una hora, volver, y ver franja sobre un contador
 * muerto mientras la relectura va en vuelo.
 *
 * Más de dos ciclos es más viejo que cualquier valor que la cadencia normal
 * llegue a servir. NO se puede apoyar en el margen de 100 de la banda: ese
 * margen se dimensionó para el retraso del BACKEND al ritmo medido (`CO-12`),
 * no para vejez del cliente.
 *
 * DE QUÉ DEPENDE QUE ESTO FUNCIONE, Y NO ESTÁ EN ESTE ARCHIVO (hallazgo de la
 * revisión arquitectónica). Lo que garantiza que con el compositor ABIERTO esta
 * cota nunca se alcance no es la cadencia de 60 s: es el `timeout: 30_000`
 * global de `src/axios/axios.singleton.ts`. Con 30 s duros ninguna lectura
 * sobrevive un ciclo de poll, así que un `ready` sólo puede enranciarse con la
 * compuerta cerrada. Si alguien sube ese timeout por encima de 60 s aparecen
 * lecturas solapadas y la cota pasa a ser alcanzable con una lectura en vuelo.
 * Ese archivo no lo gobierna este ticket y nada lo fija.
 *
 * Y una deuda conocida que de ahí cuelga: `readAt` se estampa al RESOLVER, no
 * al pedir, así que una respuesta que tarda N ms compra N ms de frescura extra.
 * Hoy está acotado a esos 30 s; si el timeout sube, esto también.
 */
export const FREE_TIER_READY_MAX_AGE_MS = 120_000;

/**
 * ¿La lectura estampada en `readAt` todavía puede decidir, mirada desde `now`?
 *
 * El `now` es un parámetro y no una llamada a `Date.now()` adentro, para que
 * quien juzga lo haga con el reloj de su propio render y la prueba pueda medir
 * el filo sin congelar relojes.
 *
 * `edad >= 0` es deliberado: con el reloj corriendo HACIA ATRÁS —un ajuste de
 * NTP— la resta se vuelve negativa, y una edad negativa pasaría por fresca
 * indefinidamente. El daño de ese caso cae del lado prohibido (franja sobre un
 * dato muerto), así que un reloj en el que no se puede confiar se trata como
 * vejez. El cero sí es fresco: es la lectura que acaba de aterrizar.
 */
export function esEstampaFresca(readAt: number, now: number): boolean {
  const edad = now - readAt;

  return edad >= 0 && edad < FREE_TIER_READY_MAX_AGE_MS;
}

/**
 * RET-2079 · cuántos mensajes de servicio le quedan a la franja, o `null` si
 * el contador no permite afirmarlo. Es la aritmética COMÚN de las dos
 * preguntas que se le hacen al contador, y quien la responde tiene que ser uno
 * solo para que no puedan separarse en la disciplina de los nulos:
 *
 *   - el SELLO (`isFreeTierBandOpen`, `useComposerServiceSeal.ts`) afirma
 *     «Gratis» con `restantes > margen de 100`;
 *   - el GUARDIA de saldo del compositor (`isBlocked`, mismo archivo) suelta la
 *     barra con `restantes > 0`, sin margen (D-2 de RET-2079).
 *
 * El umbral es de cada quien porque los dos fallan hacia lados OPUESTOS: el
 * sello afirma sobre precio y ante la duda calla, el guardia protege dinero y
 * ante la duda no suelta. Lo que comparten es esto: `null` quiere decir «no se
 * puede saber», y NO es «cero restantes».
 *
 * `null` cuando no hay contador, cuando `available` no es exactamente `true`
 * —un `false` es el backend NEGÁNDOSE a afirmar, con cinco razones y ninguna es
 * «cupo agotado»— o cuando falta `cap` o `total`. `== null` (no `===`) a
 * propósito: atrapa `null` Y `undefined`. El contrato dice que `cap` viaja
 * SIEMPRE, pero el front va a S3/CloudFront y el BFF a EKS por separado, y
 * durante la ventana de un despliegue la clave puede llegar ausente, no `null`.
 * Con `===` eso fallaba cerrado por ACCIDENTE ARITMÉTICO (`undefined - total`
 * es `NaN`, y `NaN > 0` es `false`), no por esta guardia; con `==` es la MISMA
 * incógnita que `null` y se lee igual. El tope nunca se suple con una
 * constante (UC-12): sin `cap` no hay restantes que calcular.
 *
 * Con la franja agotada `total` puede PASAR de `cap` (el contador suma por
 * workspace, y WBS cuenta por número), así que el resultado puede ser negativo:
 * eso es «agotada», y se lee igual que cero.
 *
 * NO juzga la vejez de la lectura ni el `status` del contexto: eso es del
 * llamador, que es quien tiene el reloj de SU render (`esEstampaFresca`).
 */
export function freeTierRemaining(
  counter: tFreeTierCounter | null,
): number | null {
  if (!counter || counter.available !== true) return null;
  if (counter.cap == null || counter.total == null) return null;

  return counter.cap - counter.total;
}
